import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Box, Flex, Link, VStack } from '@chakra-ui/layout'
import { Select, Switch, Text as ChakraText } from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BsShieldLock } from 'react-icons/bs'
import { colors } from '../../../../ui-kit'
import { SettingsToggleBox } from '..'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'
import { PermissionsEnum } from '../../../../utils'

const ImageSettingsSecurity = ({
  values,
  setFieldValue,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()
  
  const restrictSignedDropdownValue = useMemo(() => {
    let _val: "restrict_all" | "restrict_only_ai" | "do_not_restrict" = "do_not_restrict"

    if (values.security.signed === "true") {
      _val = "restrict_all"
    } else if (values.security.onlySignedAiRequests === "true") {
      _val = "restrict_only_ai"
    }

    return _val
  }, [values.security.onlySignedAiRequests, values.security.signed])

  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <AccordionItem>
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <BsShieldLock fontSize="1.5em" />
                {t('security')}
              </Flex>
              <Box
                background={colors.appPaleGreyThree}
                borderRadius="50%"
                padding="0.25em"
              >
                <AccordionIcon color={colors.appSlate} display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12} py={8} position="relative">
              <VStack spacing="0.75em">
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('image_restrict_unsigned')}
                    </ChakraText>
                  </Flex>
                  <ChakraText>
                    {t('image_restrict_unsigned_description')}
                    <Link
                      isExternal
                      href="https://imagekit.io/docs/media-delivery-basic-security#restrict-unsigned-urls"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appGreenishTeal">
                      {t('restrict_only_ai', 'Restrict only AI transformation requests')}
                    </ChakraText>
                    {t('image_restrict_unsigned_recommendation')}
                  </ChakraText>
                  <Select
                    name="security.onlySignedAiRequests"
                    isDisabled={!hasPermission}
                    maxWidth="250px"
                    value={restrictSignedDropdownValue}
                    onChange={(e) => {
                      const dropdownValue = e.target.value
                      if (dropdownValue === "do_not_restrict") {
                        void setFieldValue('security.onlySignedAiRequests', 'false')
                        void setFieldValue('security.signed', 'false')
                      } else if (dropdownValue === "restrict_only_ai") {
                        void setFieldValue('security.onlySignedAiRequests', 'true')
                        void setFieldValue('security.signed', 'false')
                      } else if (dropdownValue === "restrict_all") {
                        void setFieldValue('security.onlySignedAiRequests', 'true')
                        void setFieldValue('security.signed', 'true')
                      }
                    }}
                  >
                    <option value="do_not_restrict">{t('do_not_restrict', 'Do not restrict')}</option>
                    <option value="restrict_only_ai">{t('restrict_only_ai', 'Restrict only AI transformation requests')}</option>
                    <option value="restrict_all">{t('restrict_all', 'Restrict all requests')}</option>
                  </Select>
                </SettingsToggleBox>
                <SettingsToggleBox>
                  <Flex justifyContent="space-between" width="100%">
                    <ChakraText fontWeight="700">
                      {t('image_restrict_unamed')}
                    </ChakraText>
                    <Switch
                      name="security.named"
                      isDisabled={!hasPermission}
                      isChecked={values.security.named === 'true'}
                      onChange={e => {
                        void setFieldValue(e.target.name, e.target.checked.toString())
                      }}
                    />
                  </Flex>
                  <ChakraText>
                    {t('image_restrict_unamed_description')}
                    <Link
                      isExternal
                      href="https://docs.imagekit.io/features/security#restricting-unnamed-image-transformations"
                    >
                      {t('read_more')}
                    </Link>
                  </ChakraText>
                  <ChakraText>
                    {t('we_recommend')} :&nbsp;
                    <ChakraText as="span" color="appWatermelon">
                      OFF
                    </ChakraText>
                    {t('image_restrict_unamed_recommendation')}
                  </ChakraText>
                </SettingsToggleBox>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )
      }
    </PermissionGuard>
  )
}

export default ImageSettingsSecurity
